import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * M4 — wait-time CDF and percentiles, sliceable by tier / buy-in / opponent.
 *
 * Buckets on `min(fee)` across the proposal's seats — the fee the match
 * actually PLAYED at. Mixed-fee pairs exist, so bucketing by the seat-0 entry's
 * own fee counts a `$10` entry that downgraded into a `$5` match as a slow
 * `$10` match. `feeGuardViolations` must stay 0.
 *
 * The slice's `tier` is the SEAT-0 entry's tier, which is what `oracle.sql`
 * groups on. A proposal that mixes tiers lands in its seat-0 slice;
 * `bool_or(tier = 'SPIN_AND_GO')` would put it in the other one.
 *
 * Marginals are not returned because the CURVE is derivable: a slice's count at
 * a threshold is `round(cumulativeShare × anchors)`, exact while `anchors` stays
 * under 10,000 (4 dp of share × 10,000 anchors is the first half-count of
 * rounding error), so any union of slices sums to the share the same SQL over
 * the union would report. Percentiles are NOT derivable that way — a union's
 * p50 is not a function of its slices' p50s — so they ride each slice and an
 * aggregate view must report no percentile rather than a blended one.
 */
export const ArenaOpsMatchmakingWaitCdfSliceSchema = z.object({
  tier: z.string(),
  buyInUsdt: ArenaOpsUsdtSchema,
  opponent: z.enum(["human", "bot"]),
  anchors: z.number().int(),
  /** Median wait, seconds at 1 dp. Every slice has at least one anchor, so it is always a real measurement. */
  p50Seconds: z.number(),
  /** 90th-percentile wait, seconds at 1 dp. The tail an operator sizes bot-concession policy against. */
  p90Seconds: z.number(),
  /** One point per sampled threshold, ascending. `cumulativeShare` is a RATIO in `[0, 1]` at 4 dp, never a percentage. */
  points: z.array(z.object({ waitSeconds: z.number(), cumulativeShare: z.string() })),
});

export const ArenaOpsMatchmakingWaitCdfResponseSchema = z.object({
  slices: z.array(ArenaOpsMatchmakingWaitCdfSliceSchema),
  feeGuardViolations: z.number().int(),
});
export type ArenaOpsMatchmakingWaitCdfResponse = z.infer<typeof ArenaOpsMatchmakingWaitCdfResponseSchema>;
